import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useInView, animate } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { EASE, scrollToId } from "./shared";
import { HERO_SLIDES } from "../data/site";
import { HeroMapSlide, HeroMapCta } from "./HeroMapSlide";

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Counter({ to, suffix = "", prefix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className="font-serif-lux text-3xl sm:text-4xl text-gold-gradient">
      {prefix}{val.toLocaleString("en-IN")}{suffix}
    </span>
  );
}

const METRICS = [
  { value: 250, suffix: "+", label: "Acres - GYC Integrated Township" },
  { value: 9, suffix: "", label: "Corridor Demand Drivers Tracked" },
  { value: 6, suffix: "", label: "Property Categories Covered" },
  { value: 100, suffix: "%", label: "RERA-Verified Listing Focus" },
];

export function Hero({ onAdvisor, onInventory }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const [slide, setSlide] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    timerRef.current = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(timerRef.current);
  }, []);
  const goToSlide = (i) => {
    setSlide(i);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
  };

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <AnimatePresence mode="sync">
          {HERO_SLIDES.map((s, i) => i === slide && (
            s.type === "map" ? (
              <motion.div
                key="map-slide"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: EASE }}
                className="absolute inset-0"
                data-testid={`hero-slide-${i}`}
              >
                <HeroMapSlide />
              </motion.div>
            ) : (
            <motion.img
              key={s.src}
              src={s.src}
              alt={s.caption}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.4, ease: EASE }}
              className="absolute inset-0 w-full h-full object-cover img-cinematic"
              data-testid={`hero-slide-${i}`}
            />
            )
          ))}
        </AnimatePresence>
        {HERO_SLIDES[slide].type !== "map" && (
          <>
            <div className={`absolute inset-0 bg-gradient-to-b ${slide === 2 ? "from-black/60" : "from-black/80"} via-black/55 to-[#0A0A0A]`} />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(10,10,10,0.75)_100%)]" />
          </>
        )}
      </motion.div>

      {HERO_SLIDES[slide].type === "map" && <HeroMapCta />}

      <div className="absolute bottom-40 lg:bottom-24 right-5 lg:right-8 z-10 flex gap-2" data-testid="hero-slide-dots">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.src || s.type}
            data-testid={`hero-slide-dot-${i}`}
            onClick={() => goToSlide(i)}
            aria-label={`Show slide ${i + 1}`}
            aria-current={i === slide ? "true" : undefined}
            className={`h-1.5 rounded-full transition-all duration-500 ${i === slide ? "w-7 bg-[#D4AF37]" : "w-3 bg-white/30 hover:bg-white/50"}`}
          />
        ))}
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-5 lg:px-8 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0, letterSpacing: "0.6em" }}
          animate={{ opacity: 1, letterSpacing: "0.42em" }}
          transition={{ duration: 1.6, delay: 0.2 }}
          className="text-[11px] uppercase text-[#D4AF37] mb-8"
          data-testid="hero-overline"
        >
          Radiance Realtors &mdash; Luxury Real Estate Advisory
        </motion.p>

        <h1 className="font-serif-lux text-5xl sm:text-6xl lg:text-8xl leading-[1.02]" data-testid="hero-headline">
          <MaskedLine delay={0.5}>Your Gateway to the</MaskedLine>
          <MaskedLine delay={0.68}>
            <span className="text-gold-gradient italic">Yamuna Growth Corridor.</span>
          </MaskedLine>
        </h1>

        <motion.div
          className="gold-hairline w-56 mt-8 origin-left"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1.4, delay: 1.15, ease: EASE }}
        />

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.25, ease: EASE }}
          className="mt-6 max-w-xl text-sm sm:text-base text-[#A1A1AA] leading-relaxed"
          data-testid="hero-subheadline"
        >
          Premium property opportunities in Gaur Yamuna City and the rapidly emerging
          ecosystem surrounding Noida International Airport.
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.45 }}
          className="mt-4 text-[11px] uppercase tracking-[0.3em] text-[#71717A]"
          data-testid="hero-descriptor"
        >
          Apartments &bull; Plots &bull; Villas &bull; Commercial &bull; Resale &bull; Investment
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-btn"
            onClick={() => scrollToId("gyc")}
            className="px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#F3E5AB] transition-colors rounded-[2px]"
          >
            Explore Gaur Yamuna City
          </button>
          <button
            data-testid="hero-advisor-btn"
            onClick={onAdvisor}
            className="px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-colors rounded-[2px]"
          >
            Speak to an Advisor
          </button>
          <button
            data-testid="hero-inventory-btn"
            onClick={onInventory}
            className="px-2 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-[#F3E5AB] underline underline-offset-8 decoration-[#D4AF37]/40 transition-colors"
          >
            Get Current Inventory
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.9 }}
          className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8 max-w-3xl"
          data-testid="hero-metrics"
        >
          {METRICS.map((m) => (
            <div key={m.label}>
              <Counter to={m.value} suffix={m.suffix} />
              <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#71717A] leading-relaxed">{m.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("difference")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-24 lg:bottom-10 left-1/2 -translate-x-1/2 z-10 text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors"
        aria-label="Scroll down"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} className="block">
          <ChevronDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
}
